/**
 * The unread badge and its drop-down.
 *
 * Lives in the shell, so it is the one piece of the product visible from every
 * screen. Two decisions worth stating:
 *
 * Opening the panel does not mark everything read. A badge that clears itself
 * because you glanced at it is a badge that loses you the message you meant to
 * come back to. Reading one marks that one; "mark all" is a deliberate click.
 *
 * A failed count renders nothing rather than an error. The bell is chrome on
 * every page in the app, and a broken count must never put an error banner
 * across somebody's attendance register.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useTenantFormatting } from "@/shared/auth/hooks";
import { Badge, Button } from "@/shared/ui";

import { useInbox, useMarkRead, useUnreadCount } from "../api/hooks";
import type { NotificationCategory } from "../api/types";

const CATEGORY_TONE: Record<NotificationCategory, "neutral" | "warning" | "danger" | "brand"> = {
  attendance: "warning",
  academics: "brand",
  fees: "brand",
  announcements: "neutral",
  events: "neutral",
  system: "neutral",
  security: "danger",
};

export function NotificationBell({ inboxPath }: { inboxPath: string }) {
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const unread = useUnreadCount();
  const inbox = useInbox();
  const markRead = useMarkRead();
  const { formatDateTime } = useTenantFormatting();

  // Close on an outside click or Escape — a panel that traps you is worse than
  // no panel.
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const count = unread.data?.unread ?? 0;
  const rows = (inbox.data?.data ?? []).slice(0, 6);

  return (
    <div className="relative" ref={panel}>
      <button
        type="button"
        aria-label={count > 0 ? `Notifications, ${count} unread` : "Notifications"}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--border-subtle)] text-base"
      >
        <span aria-hidden>◔</span>
        {count > 0 && (
          <span
            aria-hidden
            className="absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--brand)] px-1 text-[10px] font-semibold text-white"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="surface absolute end-0 z-50 mt-2 w-[22rem] rounded-xl border shadow-lg">
          <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-3 py-2">
            <p className="text-sm font-semibold">Notifications</p>
            {count > 0 && (
              <Button
                size="sm"
                variant="ghost"
                loading={markRead.isPending}
                onClick={() => markRead.mutate(undefined)}
              >
                Mark all read
              </Button>
            )}
          </div>

          {rows.length === 0 && (
            <p className="text-muted px-3 py-6 text-center text-sm">Nothing yet.</p>
          )}

          <ul className="max-h-96 overflow-y-auto">
            {rows.map((row) => (
              <li
                key={row.id}
                className={`border-b border-[var(--border-subtle)] last:border-0 ${
                  row.read_at ? "" : "bg-[var(--brand)]/5"
                }`}
              >
                <button
                  type="button"
                  // Reading one marks one. Opening the panel marks nothing.
                  onClick={() => !row.read_at && markRead.mutate([row.id])}
                  className="w-full px-3 py-2.5 text-start"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium">{row.subject ?? row.template_key}</p>
                    <Badge tone={CATEGORY_TONE[row.category] ?? "neutral"}>{row.category}</Badge>
                  </div>
                  <p className="text-muted mt-0.5 line-clamp-2 text-xs">{row.body}</p>
                  <p className="text-muted mt-1 text-[11px]">{formatDateTime(row.created_at)}</p>
                </button>
              </li>
            ))}
          </ul>

          <div className="border-t border-[var(--border-subtle)] px-3 py-2 text-center">
            <Link
              to={inboxPath}
              onClick={() => setOpen(false)}
              className="text-sm text-[var(--brand)] hover:underline"
            >
              See all
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
